import React, { useState, useEffect } from "react";
import "./ExperienceStyles.css";
import ithreexLogo from "../../assets/ithreexglobal_logo.jpg";
import bluerabbitLogo from "../../assets/bluerabbitsocial_logo.jpg";

const Experience = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const experiences = [
    {
      company: "Ithreex Global",
      role: "FullStack Developer",
      type: "Full-time",
      location: "Córdoba",
      period: "Nov 2024 - Apr 2026",
      logo: ithreexLogo,
      description: "Contributed to the full lifecycle of both client-facing applications and internal AI-driven products, including e-commerce platforms, business management systems, and custom solutions leveraging artificial intelligence.",
      responsibilities: [
        "Develop and maintain scalable, responsive web applications using modern stacks (React, Angular, Next.js, Tailwind, Node.js)",
        "Participate in system architecture design, feature scoping, and technical planning",
        "Collaborate on AI-driven initiatives, including the development of chatbots and other intelligent automation features",
        "Ensure consistent and accessible UI/UX across all products, working closely with design teams",
        "Perform quality assurance tasks such as testing, debugging, and code reviews to maintain high development standards",
        "Coordinate with cross-functional teams to deliver customized software and AI solutions aligned with client and business needs"
      ],
      tech: [
        "React", "Angular", "Next.js", "Node.js", "TypeScript", "TailwindCSS", "MongoDB", 
        "PostgreSQL", "Redis", "Docker", "AWS", "Git", "Jest", "Cypress",
        "AI/ML", "ChatGPT API", "OpenAI", "E-commerce", "REST APIs", "N8N", "MCP", "FastAPI"
      ]
    },
    {
      company: "Bluerabbit",
      role: "FrontEnd Developer",
      type: "Contractor",
      location: "Buenos Aires, Argentina",
      period: "Oct 2022 - Jul 2024",
      logo: bluerabbitLogo,
      description: "Developed user interfaces for mobile and web applications utilizing modern technologies.",
      responsibilities: [
        "Developed user interfaces for mobile and web applications utilizing modern technologies such as React Native, Expo, Redux, React Query, ReactJS, Next.js, TypeScript, Firebase, Node.js, and TailwindCSS",
        "Conducted thorough testing of app performance and usability to ensure compatibility and functionality, and developed comprehensive documentation to support end-users",
        "Designed responsive interfaces to ensure optimal user experience across devices",
        "Implemented chatbot IA development and metrics analytics across various platforms"
      ],
      tech: [
        "React Native", "Expo", "TypeScript", "Firebase", "Redux", "React Query",
        "ReactJS", "Next.js", "Node.js", "TailwindCSS", "JavaScript", "HTML5", "CSS3",
        "Git", "GitHub", "Figma", "Adobe XD", "Jest", "Testing Library", "Analytics"
      ]
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const nextExperience = () => {
    setActiveIndex((prev) => (prev + 1) % experiences.length);
  };

  const prevExperience = () => {
    setActiveIndex((prev) => (prev - 1 + experiences.length) % experiences.length);
  };

  return (
    <div className="experience-container">
      <div className="experience-header">
        <h1>Work Experience</h1>
        <p>My professional journey in software development</p>
      </div>
      
      <div className={`experience-showcase ${isVisible ? 'visible' : ''}`}>
        <div className="experience-navigation">
          <button 
            className="nav-btn prev-btn" 
            onClick={prevExperience}
            aria-label="Previous experience"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          
          <div className="experience-indicators">
            {experiences.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === activeIndex ? 'active' : ''}`}
                onClick={() => setActiveIndex(index)}
                aria-label={`Go to experience ${index + 1}`}
              />
            ))}
          </div>
          
          <button 
            className="nav-btn next-btn" 
            onClick={nextExperience}
            aria-label="Next experience"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>

        <div className="experience-card">
          <div className="card-header">
            <div className="company-badge">
              <div className="badge-icon">
                <img 
                  src={experiences[activeIndex].logo} 
                  alt={`${experiences[activeIndex].company} logo`}
                  className="company-logo"
                />
              </div>
              <div className="company-info">
                <h3 className="company-name">{experiences[activeIndex].company}</h3>
                <span className="role">{experiences[activeIndex].role}</span>
              </div>
            </div>
            
            <div className="experience-meta">
              <div className="meta-item">
                <span className="meta-label">Type</span>
                <span className="meta-value">{experiences[activeIndex].type}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Location</span>
                <span className="meta-value">{experiences[activeIndex].location}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Period</span>
                <span className="meta-value">{experiences[activeIndex].period}</span>
              </div>
            </div>
          </div>

          <div className="card-body">
            <p className="description">{experiences[activeIndex].description}</p>
            
            <div className="tech-stack">
              <h4>Technologies & Skills</h4>
              <div className="tech-tags">
                {experiences[activeIndex].tech.map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>

            <div className="responsibilities-section">
              <h4>Key Responsibilities</h4>
              <ul className="responsibilities-list">
                {experiences[activeIndex].responsibilities.map((resp, index) => (
                  <li key={index} className="responsibility-item">
                    <span className="bullet"></span>
                    {resp}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Experience;
